import React, { useCallback, useState } from 'react';
import { Crown, Landmark, UserCog } from 'lucide-react';
import { Sizes } from 'enums/sizes';
import { Button } from 'components/ui/Button';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { Column, TableAction } from 'components/display/Table/types';
import { FetchDataParams, TableResponse } from 'shared/contexts/table-context/types';
import { usePanel } from 'hooks/usePanel';
import { attributionsPlateformesAPI } from 'services/swagger-api';
import { CabinetBriquesPlateformeDto } from 'api/attributions-plateformes';
import { NatureChip } from 'back-office/shared/components/rolesUi';
import { CollaboratorsAvatarStack } from 'back-office/shared/components/collaborators/CollaboratorsAvatarStack';
import { CabinetCollaborateursPanel } from './CabinetCollaborateursPanel';

interface CabinetsRolesCardProps {
  /** Fourni = mode ADMIN (fiche plateforme). Absent = mode GOUVERNANCE :
   * la liste et les droits sont scopés serveur sur la plateforme active de
   * l'appelant, et le plafond délégable borne l'édition. */
  plateformeId?: string;
}

/** Ligne du tableau : le DTO + l'id requis par le ServerTable. */
type LigneCabinet = CabinetBriquesPlateformeDto & { id: string };

/**
 * Rôles de la plateforme, au grain PERSONNE MORALE : la liste (paginée et
 * cherchée côté serveur — une plateforme à 1000 courtiers reste navigable)
 * montre les cabinets ; cliquer sur un cabinet ouvre le panel de SES membres,
 * puis l'édition des briques d'un membre. Partagé entre la fiche plateforme
 * (admin) et le Portail Plateforme (gouvernance).
 */
export const CabinetsRolesCard: React.FC<CabinetsRolesCardProps> = ({ plateformeId }) => {
  const { openPanel } = usePanel();

  // Mode de gestion de la plateforme (porté par la réponse liste) : sur une
  // plateforme Administrée, la PM principale la REPRÉSENTE sans la gérer.
  const [estAdministree, setEstAdministree] = useState(false);

  const fetchData = useCallback(
    async (params: FetchDataParams): Promise<TableResponse<LigneCabinet>> => {
      const requete = {
        searchString: params.searchString || undefined,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize
      };
      const res = plateformeId
        ? await attributionsPlateformesAPI.getCabinetsBriques({ plateformeId: plateformeId, ...requete })
        : await attributionsPlateformesAPI.getCabinetsBriquesGouvernance(requete);

      setEstAdministree(res.estAdministree);

      return {
        items: res.items.map((i) => ({ ...i, id: i.personneMoraleId })),
        totalItems: res.totalCount,
        totalPages: Math.max(1, Math.ceil(res.totalCount / params.pageSize)),
        currentPage: params.pageNumber
      };
    },
    [plateformeId]
  );

  const ouvrirPanel = useCallback(
    (ligne: LigneCabinet) =>
      openPanel(
        { title: `Collaborateurs — ${ligne.designation}`, size: Sizes['2xl'] },
        <CabinetCollaborateursPanel
          plateformeId={plateformeId}
          personneMoraleId={ligne.personneMoraleId}
          cabinetName={ligne.designation}
        />
      ),
    [openPanel, plateformeId]
  );

  const columns: Column<LigneCabinet>[] = [
    {
      key: 'designation',
      label: 'Personne morale',
      align: 'left',
      render: (_, row) => (
        <span className={`flex items-center gap-2 text-gray-900 ${row.isPlateformeOwner ? 'font-bold' : 'font-medium'}`}>
          {row.designation}
          {row.isPlateformeOwner &&
            (estAdministree ? (
              <span
                title="Plateforme administrée : le back-office pilote, cette personne morale la représente sans la gérer."
                className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700"
              >
                <Landmark size={12} />
                Représente la plateforme
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                <Crown size={12} className="fill-amber-400 text-amber-600" />
                Gère la plateforme
              </span>
            ))}
        </span>
      )
    },
    {
      key: 'nature',
      label: 'Nature',
      align: 'left',
      render: (_, row) => <NatureChip nature={row.nature} libelle={row.natureLibelle} />
    },
    {
      key: 'collaborateurs',
      label: 'Collaborateurs',
      align: 'left',
      width: '220px',
      render: (_, row) => (
        <CollaboratorsAvatarStack
          members={row.collaborateurs ?? []}
          totalCount={row.collaborateursActifs}
          showPrincipalName={false}
        />
      )
    }
  ];

  const rowActions: TableAction<LigneCabinet>[] = [
    {
      label: 'collaborateurs',
      render: (row: LigneCabinet, index: number) => (
        <Button key={index} color="slate" round tooltip="Gérer les rôles des collaborateurs" onClick={() => ouvrirPanel(row)}>
          <UserCog size={16} />
        </Button>
      )
    }
  ];

  return (
    <ServerTable<LigneCabinet>
      apiConfig={{ fetchData }}
      columns={columns}
      enableSearch
      searchPlaceholder="Rechercher une personne morale..."
      pageSize={10}
      showPaginationInfo
      centerColumns
      onRowClick={ouvrirPanel}
      getRowClassName={(row) =>
        row.isPlateformeOwner
          ? `cursor-pointer ${estAdministree ? 'bg-indigo-50/60' : 'bg-amber-50/70'}`
          : 'cursor-pointer'
      }
      actions={{ row: rowActions }}
    />
  );
};
